import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, UrlTree } from '@angular/router';
import { CognitoAuthService } from '@auth/services/cognito-auth.service';
import { NavigationKey } from '@app/generated/graphql';
import { NavigationService } from '@app/shared/services/navigation.service';
import { RdrToastService } from '@ui/services/rdr-toast.service';
import { Observable, concatMap, map, take } from 'rxjs';

/**
 * EC-338 / Permission v1 Wave 8: route visibility from `userAuth.navigation` (Pattern 4).
 *
 * One guard for every keyed route — it reads `route.data.navKey` and asks nothing else.
 * A route without a key (currently only the excluded superadmin profile) passes through.
 *
 * EC-758: a route that needs several keys lists them in `route.data.navKeys` and opens only
 * when every one of them is held (a pipeline board needs the section key and its shell key).
 */
@Injectable({
  providedIn: 'root',
})
export class NavigationGuard {
  constructor(
    private cognitoAuthService: CognitoAuthService,
    private navigationService: NavigationService,
    private toastService: RdrToastService,
    private router: Router,
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    const navKeys = (route.data?.navKeys as NavigationKey[] | undefined) ?? [];
    const navKey = route.data?.navKey as NavigationKey | undefined;
    const requiredKeys = navKey ? [navKey, ...navKeys] : navKeys;

    if (!requiredKeys.length) {
      return true;
    }

    return this.cognitoAuthService.hasCognitoSession().pipe(
      concatMap(() => this.navigationService.navigation$),
      take(1),
      map((keys) => {
        if (requiredKeys.every((key) => keys.includes(key))) {
          return true;
        }

        this.toastService.info({
          message: 'This page has restricted access',
          config: { closeButton: true },
        });

        // Redirect instead of cancelling: a cancelled initial navigation
        // leaves a blank app shell without the header.
        return this.router.parseUrl('/');
      }),
    );
  }
}
